/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        ProfilePage.tsx
 * @description Page de gestion du profil utilisateur
 *              Permet de modifier les informations personnelles, le mot de passe
 *              et de configurer la double authentification (2FA)
 *
 * @version     2.5
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';
import { profileService, type ProfileUpdateData } from '@/services/profileService';
import { twoFactorService } from '@/services/twoFactorService';
import { TwoFactorSetup } from '@/components/auth/TwoFactorSetup';
import {
  UserIcon,
  KeyIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

/**
 * Page de profil utilisateur
 *
 * Sections :
 * - Informations personnelles (nom, prenom, email, telephone)
 * - Securite (changement de mot de passe)
 * - Double authentification (activation/desactivation 2FA)
 */
export function ProfilePage() {
  const { user, setUser } = useAuthStore();

  // Etat du formulaire d'informations personnelles
  const [profileData, setProfileData] = useState<ProfileUpdateData>({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
  });
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  // Etat du formulaire de mot de passe
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);

  // Etat 2FA
  const [isTotpEnabled, setIsTotpEnabled] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [isDisabling2FA, setIsDisabling2FA] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);

  // Charger les donnees du profil au montage
  useEffect(() => {
    if (user) {
      setProfileData({
        nom: user.nom || '',
        prenom: user.prenom || '',
        email: user.email || '',
        telephone: user.telephone || '',
      });
      // Verifier si la 2FA est activee
      checkTwoFactorStatus();
    }
  }, [user]);

  const checkTwoFactorStatus = async () => {
    try {
      const status = await twoFactorService.getStatus();
      setIsTotpEnabled(status.enabled);
    } catch {
      // 2FA non configuree ou erreur - considerer comme desactivee
      setIsTotpEnabled(false);
    }
  };

  // Gestion du formulaire d'informations personnelles
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProfileLoading(true);

    try {
      const response = await profileService.updateProfile(profileData);
      setUser(response.user);
      toast.success('Profil mis a jour avec succes');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la mise a jour';
      toast.error(message);
    } finally {
      setIsProfileLoading(false);
    }
  };

  // Gestion du formulaire de mot de passe
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }

    setIsPasswordLoading(true);

    try {
      await profileService.changePassword(passwordData.oldPassword, passwordData.newPassword);
      toast.success('Mot de passe modifie avec succes');
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors du changement de mot de passe';
      toast.error(message);
    } finally {
      setIsPasswordLoading(false);
    }
  };

  // Gestion 2FA
  const handleTwoFactorSetupComplete = () => {
    setShowTwoFactorSetup(false);
    setIsTotpEnabled(true);
    toast.success('Double authentification activee');
  };

  const handleDisable2FA = async () => {
    if (!disablePassword) {
      toast.error('Veuillez saisir votre mot de passe');
      return;
    }

    setIsDisabling2FA(true);
    try {
      await twoFactorService.disable(disablePassword);
      setIsTotpEnabled(false);
      setShowDisableModal(false);
      setDisablePassword('');
      toast.success('Double authentification desactivee');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la desactivation';
      toast.error(message);
    } finally {
      setIsDisabling2FA(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Mon profil</h1>

      <div className="space-y-6">
        {/* Section Informations personnelles */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center mb-4">
              <UserIcon className="w-6 h-6 text-primary-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Informations personnelles</h2>
            </div>

            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="prenom" className="label">
                    Prenom
                  </label>
                  <input
                    id="prenom"
                    name="prenom"
                    type="text"
                    value={profileData.prenom}
                    onChange={handleProfileChange}
                    className="input"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="nom" className="label">
                    Nom
                  </label>
                  <input
                    id="nom"
                    name="nom"
                    type="text"
                    value={profileData.nom}
                    onChange={handleProfileChange}
                    className="input"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="label">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  className="input"
                  required
                />
              </div>

              <div>
                <label htmlFor="telephone" className="label">
                  Telephone
                </label>
                <input
                  id="telephone"
                  name="telephone"
                  type="tel"
                  value={profileData.telephone || ''}
                  onChange={handleProfileChange}
                  className="input"
                  placeholder="06 12 34 56 78"
                />
              </div>

              <div className="flex justify-end">
                <button type="submit" disabled={isProfileLoading} className="btn-primary">
                  {isProfileLoading ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Section Securite - Mot de passe */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center mb-4">
              <KeyIcon className="w-6 h-6 text-primary-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Changer le mot de passe</h2>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label htmlFor="oldPassword" className="label">
                  Mot de passe actuel
                </label>
                <input
                  id="oldPassword"
                  name="oldPassword"
                  type="password"
                  value={passwordData.oldPassword}
                  onChange={handlePasswordChange}
                  className="input"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="newPassword" className="label">
                    Nouveau mot de passe
                  </label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className="input"
                    minLength={6}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 6 caracteres</p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="label">
                    Confirmer le mot de passe
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className="input"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button type="submit" disabled={isPasswordLoading} className="btn-primary">
                  {isPasswordLoading ? 'Modification...' : 'Modifier le mot de passe'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Section Double authentification */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center mb-4">
              <ShieldCheckIcon className="w-6 h-6 text-primary-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Double authentification (2FA)</h2>
            </div>

            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center">
                  {isTotpEnabled ? (
                    <>
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                      <span className="text-green-700 font-medium">Activee</span>
                    </>
                  ) : (
                    <>
                      <ExclamationCircleIcon className="w-5 h-5 text-yellow-500 mr-2" />
                      <span className="text-yellow-700 font-medium">Desactivee</span>
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {isTotpEnabled
                    ? 'Votre compte est protege par la double authentification.'
                    : 'Renforcez la securite de votre compte avec la double authentification.'}
                </p>
              </div>

              <div>
                {isTotpEnabled ? (
                  <button
                    onClick={() => setShowDisableModal(true)}
                    className="btn-secondary text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Desactiver
                  </button>
                ) : (
                  <button onClick={() => setShowTwoFactorSetup(true)} className="btn-primary">
                    Configurer
                  </button>
                )}
              </div>
            </div>

            {/* Modal de configuration 2FA */}
            {showTwoFactorSetup && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                  <TwoFactorSetup
                    onComplete={handleTwoFactorSetupComplete}
                    onCancel={() => setShowTwoFactorSetup(false)}
                  />
                </div>
              </div>
            )}

            {/* Modal de desactivation 2FA */}
            {showDisableModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Desactiver la double authentification
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Etes-vous sur de vouloir desactiver la double authentification ? Votre compte
                    sera moins securise.
                  </p>

                  <div className="mb-4">
                    <label htmlFor="disablePassword" className="label">
                      Confirmez avec votre mot de passe
                    </label>
                    <input
                      id="disablePassword"
                      type="password"
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                      className="input"
                      placeholder="Votre mot de passe"
                    />
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowDisableModal(false);
                        setDisablePassword('');
                      }}
                      className="btn-secondary"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleDisable2FA}
                      disabled={isDisabling2FA}
                      className="btn-primary bg-red-600 hover:bg-red-700"
                    >
                      {isDisabling2FA ? 'Desactivation...' : 'Desactiver'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Information sur les roles */}
        <div className="card bg-gray-50">
          <div className="card-body">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Vos roles</h3>
            <div className="flex flex-wrap gap-2">
              {user?.roles?.map((role) => (
                <span
                  key={role}
                  className="px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded"
                >
                  {role.replace('ROLE_', '').replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
